import { fmtN, fmtP, toneText, PaceArrow, PaceBadge, VsLastMonth, VsPrevWeek } from "@/components/overview/Momentum";
import { fmtRs, tdLabel, planLabel } from "./SvcBits";

const REV = [
  { key: "total_gs", label: "Total GS revenue", size: "lg" },
  { key: "labour", label: "Labour", size: "md" },
  { key: "parts", label: "Parts", size: "md" },
  { key: "ccs", label: "CCS", size: "md" },
  { key: "accs", label: "Accessories", size: "md" },
  { key: "warranty", label: "Warranty", size: "md" },
  { key: "body_shop", label: "Body shop", size: "md" },
];
const ROS = [
  { key: "gs_ros", label: "GS ROs", size: "md" },
  { key: "fs", label: "FS ROs", size: "sm" },
  { key: "ps", label: "PS ROs", size: "sm" },
  { key: "rr", label: "RR ROs", size: "sm" },
  { key: "ar", label: "AR ROs", size: "sm" },
  { key: "acc", label: "ACC ROs", size: "sm" },
];
const fmtDay = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "");

function Tile({ k, label, size, t, period, money }) {
  const big = size === "lg";
  const isWeek = period?.kind === "week";
  const future = period?.future;
  const running = !isWeek || period?.current;
  const f = money ? fmtRs : (v) => fmtN(v);
  const hasTarget = t.plan != null;
  return (
    <div className={`border bg-card rounded-md min-w-0 flex-1 ${big ? "p-4 ring-1 ring-foreground/30 basis-[220px]" : size === "sm" ? "p-2.5 basis-[120px]" : "p-3 basis-[150px]"}`} data-testid={`sov-tile-${k}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
        {!future && <PaceArrow m={t.momentum?.pace} compact testId={`sov-tile-${k}-pace`} />}
      </div>
      <div className={`num font-bold leading-none mt-1 ${future ? "text-muted-foreground" : hasTarget ? toneText(t.pct) : ""} ${big ? "text-4xl" : size === "sm" ? "text-xl" : "text-2xl"}`} data-testid={`sov-tile-${k}-pct`}>{hasTarget ? fmtP(t.pct) : f(t.ach)}</div>
      {hasTarget ? (
        <div className="text-[11px] mt-1.5 num"><b data-testid={`sov-tile-${k}-ach`}>{f(t.ach)}</b> <span className="text-muted-foreground">of <span data-testid={`sov-tile-${k}-ptd`}>{f(t.plan_td)}</span> {tdLabel(period)} · {planLabel(period)} <span data-testid={`sov-tile-${k}-plan`}>{f(t.plan)}</span></span></div>
      ) : (
        <div className="text-[10px] mt-1.5 text-muted-foreground" data-testid={`sov-tile-${k}-notarget`}>{future ? "no achievement yet" : "no target for this line"}</div>
      )}
      {future ? (
        <div className="text-[10px] text-muted-foreground mt-0.5" data-testid={`sov-tile-${k}-future`}>Week not started{hasTarget ? ` — target ${f(t.plan)}` : ""}</div>
      ) : (
        <div className="text-[10px] text-muted-foreground num mt-0.5">
          {hasTarget && <>Gap {f(t.gap)}{running && <> · proj <b className="text-foreground" data-testid={`sov-tile-${k}-proj`}>{f(t.projected)}</b> · need <b className="text-foreground" data-testid={`sov-tile-${k}-req`}>{f(t.required_rate)}</b>/day</>} · {isWeek ? "week" : "month"} {fmtP(t.full_pct)}</>}
          {!hasTarget && running && <>run-rate <b className="text-foreground">{f(t.run_rate)}</b>/day · proj <b className="text-foreground">{f(t.projected)}</b></>}
        </div>
      )}
      {money && !future && t.per_ro != null && <div className="text-[10px] num mt-0.5" data-testid={`sov-tile-${k}-perro`}><b>₹{fmtN(t.per_ro)}</b>/RO <span className="text-muted-foreground">· best ₹{fmtN(t.best_per_ro)}</span></div>}
      {k === "gs_ros" && !future && t.appointment_share != null && <div className="text-[10px] text-muted-foreground num mt-0.5" data-testid="sov-tile-gs-ros-shares">appointment {fmtP(t.appointment_share)} · RSA {fmtP(t.rsa_share)}</div>}
      {!future && (
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
          {running && hasTarget && <PaceBadge p={t.momentum?.pace_vs_plan} testId={`sov-tile-${k}-pace-plan`} />}
          {isWeek ? <VsPrevWeek v={t.momentum?.vs_prev_week} testId={`sov-tile-${k}-vs-pw`} /> : <VsLastMonth v={t.momentum?.vs_last_month} testId={`sov-tile-${k}-vs-lm`} />}
        </div>
      )}
    </div>
  );
}

const MODES = [["gs", "GS"], ["bp", "BP"], ["total", "Total"]];
const MODE_TITLE = { gs: "GS", bp: "BP", total: "GS + BP" };
const MODE_LABELS = { bp: { total_gs: "Total BP revenue", gs_ros: "BP ROs (AR)" }, total: { total_gs: "Total GS + BP revenue", gs_ros: "GS + BP ROs" } };
const labelFor = (mode, o) => MODE_LABELS[mode]?.[o.key] || o.label;

function ModeToggle({ mode, onChange }) {
  return (
    <div className="inline-flex rounded-md border bg-card p-0.5 text-[11px] font-semibold" role="tablist" aria-label="Block A view" data-testid="sov-tiles-mode">
      {MODES.map(([k, label]) => (
        <button key={k} type="button" role="tab" aria-selected={mode === k} onClick={() => onChange(k)} data-testid={`sov-tiles-mode-${k}`}
          className={`px-2.5 py-1 rounded transition-colors ${mode === k ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}>{label}</button>
      ))}
    </div>
  );
}

export function SvcTiles({ tiles, tilesBp, tilesTotal, period, exWarranty, mode = "gs", onMode }) {
  const isWeek = period?.kind === "week";
  const base = isWeek ? `A · Revenue & ROs — ${period.week} (${fmtDay(period.start)} – ${fmtDay(period.end)})${period.current ? " · current week" : period.future ? " · not started" : " · completed"}` : "A · Revenue & ROs — month-to-date";
  const title = `${base} (${MODE_TITLE[mode]})`;
  const t = (mode === "bp" ? tilesBp : mode === "total" ? tilesTotal : tiles) || tiles;
  const rev = REV.filter((o) => !(exWarranty && o.key === "warranty") && t[o.key]);
  const ros = ROS.filter((o) => t[o.key]);
  const perRo = mode === "bp" ? "₹/RO divides by AR ROs" : mode === "total" ? "₹/RO divides by GS + AR ROs" : "₹/RO divides by GS ROs (Body shop by AR ROs)";
  const scope = mode === "bp" ? "BP = Body Shop (Accidental Repair) billing plus AR-attributed warranty · targets = Body Shop targets" : mode === "total" ? "Total = GS + BP summed tile by tile (targets, achievement, gap, projection and required rate added before any %)" : "GS revenue includes warranty labour and parts unless Ex-warranty is on";
  return (
    <div data-testid="sov-tiles" data-mode={mode}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="font-heading text-sm font-semibold" data-testid="sov-tiles-title">{title}</h3>
        <ModeToggle mode={mode} onChange={onMode} />
      </div>
      <div className="flex flex-wrap items-stretch gap-1 min-w-0" data-testid="sov-tiles-rev">
        {rev.map((o) => <Tile key={o.key} k={o.key} label={labelFor(mode, o)} size={o.size} t={t[o.key]} period={period} money />)}
      </div>
      <div className="flex flex-wrap items-stretch gap-1 min-w-0 mt-1" data-testid="sov-tiles-ro">
        {ros.map((o) => <Tile key={o.key} k={o.key} label={labelFor(mode, o)} size={o.size} t={t[o.key]} period={period} />)}
      </div>
      <p className="text-[10px] text-muted-foreground mt-1.5">
        {isWeek
          ? `Ach % = week Ach ÷ WTD target (week target = month target × the week's share of working days; running week pro-rated by working days elapsed) · ▲▶▼ = today vs the 3-day average (±15 %) · pace badge = 3-day rate ÷ required daily rate for the rest of the week · vs previous week = Ach-to-date against the previous week at the same working-day count · ${perRo} · ${scope}`
          : `Ach % = Ach MTD ÷ MTD target (month target × working days elapsed ÷ working days from the target entry) · ROs = distinct ROs invoiced · ${perRo} · ▲▶▼ = 5-day pace vs the previous 5 working days (±15 %) · pace badge = last-5-day rate ÷ required daily rate · ${scope}`}
      </p>
    </div>
  );
}
